<script setup lang="ts">
import {computed, nextTick, onBeforeUnmount, onMounted, ref, watch} from "vue";
import {useCrossFilterStore} from "@/stores/crossFilterStore.ts";
import {QuestionFieldsPresenter} from "@/utils/presenters/QuestionFieldsPresenter.ts";
import QuestionFieldsEditor from "@/components/questionFields/QuestionFieldsEditor.vue";
import Translator from "@/utils/common/Translator.ts";
import DTOBIChartLine = namaTypes.DTOBIChartLine;
import ObjectChecker from "@/utils/common/ObjectChecker.ts";

const APPLY_DEBOUNCE_MS = 400;

const props = defineProps<{
  chartLine: DTOBIChartLine;
}>();

const emit = defineEmits<{
  filtersChanged: []
}>();

const crossFilterStore = useCrossFilterStore();
const presenter = new QuestionFieldsPresenter();
const editorReady = ref(false);
const isInitializing = ref(false);

const questionField = computed(() => props.chartLine.crossFilterControlQuestionField);
const editorField = computed(() => questionField.value
    ? {...questionField.value, hideTitle: true, hideBorder: true}
    : undefined);
const questionFields = computed(() => editorField.value ? [editorField.value] : []);
const code = computed(() => questionField.value?.id);

const hasValue = computed(() => {
  if (!code.value) return false;
  return !!crossFilterStore.filledQuestions.get(code.value)?.value;
});

let debounceHandle: ReturnType<typeof setTimeout> | null = null;

async function initialize() {
  if (!questionField.value)
    return;
  isInitializing.value = true;
  // Reset before reseeding — `presenter.edit()` won't clear fields that have no default value,
  // so without this an empty seed (e.g. after clear) leaves the previous value stuck in the editor.
  presenter.record.value = {};
  const existing = code.value ? crossFilterStore.filledQuestions.get(code.value) : undefined;
  const seed = existing ? [{id: existing.id, value: existing.value}] : [];
  await presenter.edit(questionFields.value, seed);
  editorReady.value = true;
  await nextTick();
  isInitializing.value = false;
}

function applyFromPresenter() {
  if (!code.value) return;
  const filled = presenter.getQuestionFields();
  const value = (filled.find(f => f.id === code.value)?.value ?? '').trim();
  const currentStoreValue = (crossFilterStore.filledQuestions.get(code.value)?.value ?? '').trim();
  if (ObjectChecker.areEqual(value,currentStoreValue))
    return;
  if (value)
    crossFilterStore.setFilter(code.value, value);
  else
    crossFilterStore.clearFilter(code.value);
  emit('filtersChanged');
}

function scheduleApply() {
  if (isInitializing.value) return;
  if (debounceHandle) clearTimeout(debounceHandle);
  debounceHandle = setTimeout(applyFromPresenter, APPLY_DEBOUNCE_MS);
}

async function clearFilter() {
  if (!code.value) return;
  if (debounceHandle) {
    clearTimeout(debounceHandle);
    debounceHandle = null;
  }
  crossFilterStore.clearFilter(code.value);
  await initialize();
  emit('filtersChanged');
}

onMounted(initialize);
watch(questionField, initialize);
watch(() => presenter.record.value, scheduleApply, {deep: true});

// Re-seed the editor when this filter is changed elsewhere (bar chip removed, another control
// widget for the same filter, etc.). Skip when the store change came from us — i.e. the editor
// already shows the new value — to avoid a feedback loop.
watch(() => (code.value ? crossFilterStore.filledQuestions.get(code.value) : undefined),
    async (newFilter) => {
      if (isInitializing.value) return;
      const newStoreValue = (newFilter?.value ?? '').trim();
      const editorValue = (presenter.getQuestionFields().find(f => f.id === code.value)?.value ?? '').trim();
      if (editorValue === newStoreValue) return;
      await initialize();
    },
    {deep: true});
onBeforeUnmount(() => {
  if (debounceHandle) clearTimeout(debounceHandle);
});
</script>

<template>
  <div class="tw-relative tw-w-full tw-p-2">
    <q-btn v-if="hasValue" flat dense round size="xs" icon="close" color="negative"
           class="cf-clear-btn"
           :title="Translator.translate('biClearAll')"
           @click="clearFilter"/>
    <div v-if="!questionField" class="tw-text-xs tw-text-gray-500 tw-italic" v-text="Translator.translate('biCrossFilterControlMissingRef')"/>
    <div v-else-if="editorReady" class="tw-w-full">
      <QuestionFieldsEditor :presenter="presenter" :singleColumnGroup="true" :doNotApplyThemeLayouts="true"/>
    </div>
  </div>
</template>

<style scoped>
.cf-clear-btn {
  position: absolute;
  top: 4px;
  inset-inline-end: 4px;
  z-index: 2;
}
</style>
